import React from 'react';

import { useIntl } from 'react-intl';

import { MEMBERSHIP_TYPE_RESOURCE } from 'src/features/membershipType/api/useGetMembershipType';
import { useUpdateMembershipType } from 'src/features/membershipType/api/useUpdateMembershipType';
import MembershipTypeFormModal from 'src/features/membershipType/components/forms/MembershipTypeFormModal';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { MembershipType } from 'src/features/membershipType/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

interface UpdateMembershipTypeFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    children?: React.ReactNode;
    selectedMembershipTemplate?: MembershipType;
}

function UpdateMembershipTypeFormModal<TData>({
    className,
    selectedMembershipTemplate,
    children,
}: UpdateMembershipTypeFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();
    const templateName = selectedMembershipTemplate?.translations.find((temp) => temp.language === locale)?.name ?? '';
    const { mutate, isPending } = useUpdateMembershipType({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_RESOURCE] });
            toast({
                title: formatMessage(membershipTypeMessages.updateTemplateSuccess),
                description: formatMessage(membershipTypeMessages.updateTemplateSuccessDescription, {
                    entry: templateName,
                }),
            });
        },
    });

    return (
        <MembershipTypeFormModal
            selectedMembershipTemplate={selectedMembershipTemplate}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ parkingId, id: selectedMembershipTemplate?.id || '', ...values }, { onSuccess, onError })
            }
            title={formatMessage(membershipTypeMessages.editTemplate)}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(membershipTypeMessages.editTemplateSubtitle, {
                entry: templateName,
            })}
        >
            {children}
        </MembershipTypeFormModal>
    );
}

export default UpdateMembershipTypeFormModal;
